/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IAny,
  ICarbonAvoidanceData,
  ICarbonScopeOneData,
  IDetailComponentProps,
  IDetailComponentPropsSeparateEmissionAPI,
  IRequestContext,
  ISiteCard
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Tooltip } from '@scuf/common';
import CarbonScopeOneDetailsComponent from '../CarbonScopeOneDetailWidget/CarbonScopeOneDetailsComponent';
import {
  EnergyUsageDataSourceType,
  SmartPowerSubscriptionType
} from '../../../shared/Enums';
import { ApiContext } from '../../../../context/ApiContext';

interface ITotalEmissionDetailContent extends ISiteCard {
  data: ICarbonScopeOneData;
  avoidanceData?: ICarbonAvoidanceData;
  requestContext?: IRequestContext;
  onRefresh: () => Promise<void>;
  forSummary?: boolean;
  forGraph?: boolean;
  dataSourceType?: string;
  isSite?: boolean;
  carbonAllSiteMonthData?: boolean;
}

export const UserContext = React.createContext(
  {} as IRequestContext | undefined
);

export const cardHeaderPremiumCheck = (
  requestContext: IRequestContext | undefined
) =>
  requestContext?.context.smpSubscriptionType ===
  SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
    ? 'smp-total-width'
    : '';

export const spanHeaderPremiumCheck = (
  requestContext: IRequestContext | undefined
) =>
  requestContext?.context.smpSubscriptionType ===
  SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
    ? 'emaas-summary-card-header smp-header'
    : 'emaas-summary-card-header';

const TotalEmissionDetailContent = (props: ITotalEmissionDetailContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, avoidanceData, requestContext, isSite } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        <UserContext.Provider value={requestContext}>
          <CarbonScopeOneDetailsComponent
            data={data}
            avoidanceData={avoidanceData}
            trendChartCategory={t(d.CO2)}
            isCarbonAvoidance={false}
            forSummary={props.forSummary}
            forGraph={props.forGraph}
            dataSourceType={props.dataSourceType}
            isSite
            carbonAllSiteMonthData={props.carbonAllSiteMonthData}
          />
        </UserContext.Provider>
      </div>
    </div>
  );
};

const TotalEmissionDetailWithLoader = withLoadingStatus(
  withErrorStatus(TotalEmissionDetailContent)
);

const TotalEmissionDetailWidgetSeparateAPI = (
  props: IDetailComponentPropsSeparateEmissionAPI
) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;
  const {
    avoidanceData,
    requestContext,
    dataConnector,
    dataConnectorForGraph,
    dataSourceType,
    isSite
  } = props;

  const apiContext = useContext(ApiContext);
  const { cemsEnableCarbonSourceSelect } = apiContext;

  const { isFromSmartPower } = apiContext!;

  const onRefreshPerf = () =>
    dataConnectorForGraph &&
    dataConnectorForGraph.actions &&
    dataConnectorForGraph.actions.getAll();
  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const { all, loading, error, status } = dataConnector.data;
  const carbonTotalEmissionsData = (all as unknown) as ICarbonScopeOneData;
  const isDataAvailable = carbonTotalEmissionsData.totalAggregations
    ? true
    : false;
  const isAvoidanceDataAvailable = avoidanceData ? true : false;

  const allPerf = dataConnectorForGraph?.data.all;
  const loadingPerf = dataConnectorForGraph.data.loading;
  const errorPerf = dataConnectorForGraph.data.error;
  const statusPerf = dataConnectorForGraph.data.status;
  const graphData = (allPerf as unknown) as ICarbonScopeOneData;
  const isDataAvailableForGraph =
    graphData.monthWiseAggregations && graphData.monthWiseAggregations.length
      ? true
      : false;

  const [isLoading, setIsLoading] = useState<boolean>(loadingPerf);
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(errorPerf);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);
  const [data, setData] = useState({} as ICarbonScopeOneData);
  const [dataPerf, setDataPerf] = useState({} as ICarbonScopeOneData);

  const [carbonAvoidanceData, setAvoidanceData] = useState(
    {} as ICarbonScopeOneData
  );

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (isDataAvailableForGraph || errorPerf || statusPerf || !loadingPerf) {
      if (isDataAvailableForGraph) {
        let tempData: IAny = JSON.parse(JSON.stringify(graphData));
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [
    isDataAvailableForGraph,
    statusPerf,
    loadingPerf,
    isAvoidanceDataAvailable
  ]);

  useEffect(() => {
    setIsLoading(loading);

    if (isDataAvailable || error || status || !loading) {
      if (isDataAvailable) {
        let tempData: IAny = JSON.parse(
          JSON.stringify(carbonTotalEmissionsData)
        );
        setData(tempData);
      }

      if (isAvoidanceDataAvailable) {
        setAvoidanceData(carbonAvoidanceData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status, isAvoidanceDataAvailable]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header className={cardHeaderPremiumCheck(requestContext)}>
          <span className={spanHeaderPremiumCheck(requestContext)}>
            {t(d.TOTALEMISSIONS)}
            <Tooltip
              element={
                <Icon
                  root="common"
                  name="badge-info"
                  exactSize="0.875rem"
                  className="emaas-summary-card-header-info-icon"
                />
              }
              event="hover"
              // content={t(d.TOTALEMISSIONSTOOLTIP)}
              content={
                requestContext?.context.subscriptionType ===
                'BASIC_SUBSCRIPTION'
                  ? t(d.TOTALEMISSIONSTOOLTIP)
                  : cemsEnableCarbonSourceSelect
                  ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                    ? t(d.CARBONTOOLTIPSUTILITYDETAIL)
                    : dataSourceType ===
                      EnergyUsageDataSourceType.UTILITY_INTERVAL
                    ? t(d.CARBONTOOLTIPINTRVALDETAIL)
                    : t(d.CARBONTOOLTIPLIVEDETAIL)
                  : t(d.TOTALEMISSIONSTOOLTIP)
              }
              position="right center"
              size="small"
            />
          </span>
          {requestContext?.context.smpSubscriptionType ===
            SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION && (
            <span className="emaas-summary-card-header">
              {t(d.CARBONAVOIDANCE)}
              <Tooltip
                element={
                  <Icon
                    root="common"
                    name="badge-info"
                    exactSize="0.875rem"
                    className="emaas-summary-card-header-info-icon"
                  />
                }
                event="hover"
                content={t(d.TOTALAVOIDANCETOOLTIP)}
                position="right center"
                size="small"
              />
            </span>
          )}
        </Card.Header>
        {statusCode == 204 || statusCodePerf == 204 ? (
          <Card.Content className="detail-summary-card detail-view">
            <TotalEmissionDetailWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              avoidanceData={avoidanceData ?? undefined}
              loadingText={t(e.LOADING)}
              title={t(e.CONNECTIONERRORTITLE)}
              noDataMessage={t(e.NODATAAVAILABLE)}
              connectionErrorMessage={t(e.CONNECTIONERROR)}
              requestContext={requestContext}
              forSummary={true}
              isSite
              carbonAllSiteMonthData
            />
          </Card.Content>
        ) : (
          <>
            <Card.Content className="detail-summary-card detail-view">
              <TotalEmissionDetailWithLoader
                loading={isLoading}
                error={errorInfo}
                statusCode={statusCode}
                onRefresh={onRefresh}
                data={data}
                avoidanceData={avoidanceData ?? undefined}
                loadingText={t(e.LOADING)}
                title={t(e.CONNECTIONERRORTITLE)}
                noDataMessage={t(e.NODATAAVAILABLE)}
                connectionErrorMessage={t(e.CONNECTIONERROR)}
                requestContext={requestContext}
                forSummary={true}
                isSite
                carbonAllSiteMonthData
              />
            </Card.Content>
            <Card.Content className="detail-summary-card detail-view">
              <TotalEmissionDetailWithLoader
                loading={isLoadingPerf}
                error={errorInfoPerf}
                statusCode={statusCodePerf}
                onRefresh={onRefreshPerf}
                data={dataPerf}
                avoidanceData={avoidanceData ?? undefined}
                loadingText={t(e.LOADING)}
                title={t(e.CONNECTIONERRORTITLE)}
                noDataMessage={t(e.NODATAAVAILABLE)}
                connectionErrorMessage={t(e.CONNECTIONERROR)}
                requestContext={requestContext}
                forGraph={true}
                // dataSourceType={props.dataSourceType}
                isSite
                carbonAllSiteMonthData
              />
            </Card.Content>
          </>
        )}
      </Card>
    </>
  );
};
export default TotalEmissionDetailWidgetSeparateAPI;
